import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { studentApi } from "../lib/api";
import Cal, { getCalApi } from "@calcom/embed-react";
import {
  Loader2, LogOut, ShoppingBag, ExternalLink, FileText, Video, Download,
  Calendar as CalIcon, MessageCircle, CheckCircle2, MapPin, Send, Award, Linkedin,
} from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { courseUrl } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "corsi", label: "I miei corsi" },
  { key: "materiali", label: "Materiali" },
  { key: "onboarding", label: "Onboarding" },
  { key: "date", label: "Prossime date" },
  { key: "info", label: "Chiedi info" },
];

/** LinkedIn "Add to Profile" deep-link — auto-fills the Certifications section. */
function LinkedInShareButton({ order }) {
  const completed = new Date(order.completed_at);
  const params = new URLSearchParams({
    startTask: "CERTIFICATION_NAME",
    name: order.product_name || "Corso MyDigitalAction",
    organizationName: "MyDigitalAction Experience",
    issueYear: String(completed.getFullYear()),
    issueMonth: String(completed.getMonth() + 1),
    certUrl: "https://mydigitalaction.it",
    certId: `MDA-${order.session_id.slice(-8).toUpperCase()}`,
  });
  const url = `https://www.linkedin.com/profile/add?${params.toString()}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border transition-colors"
      style={{ borderColor: "#0A66C2", color: "#0A66C2" }}
      data-testid={`linkedin-share-${order.session_id}`}
      title="Aggiungi il certificato al tuo profilo LinkedIn"
    >
      <Linkedin size={11} />
      Aggiungi a LinkedIn
    </a>
  );
}

export default function StudentArea({ onOpenBooking }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [orders, setOrders] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [dates, setDates] = useState({ events: [], configured: false });
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(!location.state?.user);
  const [activeTab, setActiveTab] = useState("corsi");

  useEffect(() => {
    (async () => {
      try {
        if (!location.state?.user) {
          const me = await studentApi.get("/auth/me");
          setUser(me.data);
        }
        const [ords, mats, cd, cfg] = await Promise.all([
          studentApi.get("/auth/my-orders"),
          studentApi.get("/auth/my-materials"),
          studentApi.get("/course-dates"),
          studentApi.get("/site-config"),
        ]);
        setOrders(ords.data);
        setMaterials(mats.data.materials || []);
        setDates(cd.data);
        setConfig(cfg.data);
      } catch (e) {
        navigate("/area-studente/login", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleLogout = async () => {
    try {
      await studentApi.post("/auth/logout");
    } catch (_) { /* ignore */ }
    navigate("/", { replace: true });
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white">
        <Loader2 size={28} className="animate-spin" style={{ color: "#d4ff3d" }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navbar onOpenBooking={onOpenBooking} />
      <section className="pt-28 lg:pt-36 pb-16">
        <div className="container-max">
          {/* HEADER */}
          <div className="flex items-start justify-between flex-wrap gap-6 mb-10">
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-14 h-14 rounded-full border border-white/10" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center font-display">
                  {user.name?.[0] || user.email[0]}
                </div>
              )}
              <div>
                <span className="section-label">Area studente</span>
                <h1 className="font-display text-3xl lg:text-4xl text-white mt-2">
                  Ciao, {user.name?.split(" ")[0] || "studente"}
                </h1>
                <p className="text-white/50 text-sm mt-1">{user.email}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm border border-white/10 rounded-full px-4 py-2 transition-colors"
              data-testid="student-logout"
            >
              <LogOut size={14} /> Esci
            </button>
          </div>

          {/* TABS */}
          <div className="border-b border-white/10 flex gap-1 overflow-x-auto mb-8 -mx-2 px-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`px-4 lg:px-5 py-3 text-sm whitespace-nowrap transition-colors relative ${
                  activeTab === t.key ? "text-white" : "text-white/50 hover:text-white/80"
                }`}
              >
                {t.label}
                {activeTab === t.key && (
                  <span className="absolute bottom-0 left-2 right-2 h-0.5" style={{ background: "#d4ff3d" }} />
                )}
              </button>
            ))}
          </div>

          {activeTab === "corsi" && <TabCourses orders={orders} onOpenBooking={onOpenBooking} />}
          {activeTab === "materiali" && <TabMaterials materials={materials} orders={orders} />}
          {activeTab === "onboarding" && <TabOnboarding user={user} config={config} />}
          {activeTab === "date" && <TabDates dates={dates} orders={orders} />}
          {activeTab === "info" && <TabInfoRequest />}
        </div>
      </section>
      <Footer />
    </div>
  );
}

/* ─────────────── I miei corsi ─────────────── */
function TabCourses({ orders, onOpenBooking }) {
  const [downloadingCert, setDownloadingCert] = useState(null);

  const downloadCertificate = async (o) => {
    setDownloadingCert(o.session_id);
    try {
      const res = await studentApi.get(`/auth/orders/${o.session_id}/certificate`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `certificato-${o.session_id.slice(-8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert("Errore nel download del certificato");
    } finally {
      setDownloadingCert(null);
    }
  };

  if (orders.length === 0) {
    return (
      <div className="bg-card border border-white/10 rounded-2xl p-8 text-center" data-testid="tab-corsi-empty">
        <ShoppingBag size={32} className="mx-auto mb-4 text-white/40" />
        <p className="text-white/60 mb-4">Non hai ancora acquistato nessun corso.</p>
        <Link to="/#corsi" className="btn-primary inline-flex items-center gap-2">
          Scopri i corsi disponibili
        </Link>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {orders.map((o) => (
        <div key={o.session_id} className="bg-card border border-white/10 rounded-2xl p-6" data-testid={`student-order-${o.session_id}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-display text-white text-lg mb-1">{o.product_name}</div>
              <div className="text-white/40 text-xs font-mono uppercase tracking-widest">
                {new Date(o.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
              </div>
              {o.completed_at && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border" style={{ background: "rgba(212,255,61,0.1)", borderColor: "rgba(212,255,61,0.35)", color: "#d4ff3d" }}>
                  <CheckCircle2 size={11} /> Completato
                </div>
              )}
            </div>
            <div className="font-display text-white text-lg" style={{ color: "#d4ff3d" }}>€{o.amount}</div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={courseUrl(o.product_id)} className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white border border-white/10 rounded-full px-3 py-1.5">
              Vedi corso <ExternalLink size={11} />
            </Link>
            <button type="button" onClick={onOpenBooking} className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white border border-white/10 rounded-full px-3 py-1.5">
              Prenota coaching
            </button>
            {o.completed_at && (
              <>
                <button
                  type="button"
                  onClick={() => downloadCertificate(o)}
                  disabled={downloadingCert === o.session_id}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 disabled:opacity-50"
                  style={{ background: "#d4ff3d", color: "#0a0a0a", fontWeight: 600 }}
                  data-testid={`certificate-${o.session_id}`}
                >
                  {downloadingCert === o.session_id ? <Loader2 size={11} className="animate-spin" /> : <Award size={11} />}
                  Scarica certificato
                </button>
                <LinkedInShareButton order={o} />
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────── Materiali ─────────────── */
function TabMaterials({ materials, orders }) {
  const [downloading, setDownloading] = useState(null);
  const productLabel = (pid) => {
    const o = orders.find((x) => x.product_id === pid);
    return o?.product_name || pid;
  };
  const download = async (m) => {
    setDownloading(m.id);
    try {
      const res = await studentApi.get(`/auth/materials/${m.id}/download`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = m.original_filename || m.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert("Errore nel download del materiale");
    } finally {
      setDownloading(null);
    }
  };

  if (materials.length === 0) {
    return (
      <div className="bg-card border border-white/10 rounded-2xl p-8 text-center" data-testid="tab-materiali-empty">
        <FileText size={32} className="mx-auto mb-4 text-white/40" />
        <div className="font-display text-white text-lg">Nessun materiale disponibile</div>
        <p className="text-white/60 text-sm mt-2 max-w-md mx-auto">
          I workbook e i bonus dei tuoi corsi appariranno qui non appena saranno pubblicati.
          Nel frattempo controlla la tua email di conferma per i link diretti.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tab-materiali-list">
      {materials.map((m) => {
        const isVideo = (m.content_type || "").startsWith("video/");
        return (
          <div key={m.id} className="bg-card border border-white/10 rounded-2xl p-6 flex items-start gap-4" data-testid={`material-${m.id}`}>
            <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 text-white">
              {isVideo ? <Video size={18} /> : <FileText size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-white text-lg leading-snug">{m.title}</div>
              <div className="text-white/40 text-xs mt-1 uppercase tracking-widest font-mono truncate">
                {productLabel(m.product_id)}
              </div>
              <button
                type="button"
                onClick={() => download(m)}
                disabled={downloading === m.id}
                className="mt-4 inline-flex items-center gap-2 text-sm rounded-full px-4 py-2 border border-white/15 text-white/85 hover:text-white hover:border-white/30 disabled:opacity-50"
                data-testid={`download-${m.id}`}
              >
                {downloading === m.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Scarica
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────── Onboarding ─────────────── */
function TabOnboarding({ user, config }) {
  const onboardingUrl = config?.cal_onboarding_url || "https://cal.com/frank-gheri/onboarding-mydigitalaction-45m";
  const calLink = onboardingUrl.replace(/^https?:\/\/(app\.)?cal\.com\//, "");

  useEffect(() => {
    (async () => {
      const cal = await getCalApi({ namespace: "onboarding-area" });
      cal("ui", {
        theme: "dark",
        hideEventTypeDetails: false,
        cssVarsPerTheme: { dark: { "cal-brand": "#d4ff3d" } },
      });
    })();
  }, []);

  return (
    <div>
      <div className="bg-card border border-white/10 rounded-2xl p-6 mb-6 flex items-start gap-4">
        <CalIcon size={22} className="flex-shrink-0 mt-0.5" style={{ color: "#d4ff3d" }} />
        <div>
          <div className="font-display text-white text-lg">Prenota la sessione di onboarding</div>
          <p className="text-white/60 text-sm mt-2">
            30 minuti in call per definire obiettivi, aspettative e la data della tua sessione in aula.
            È inclusa nel corso e serve a farti arrivare preparato al 100%.
          </p>
        </div>
      </div>
      <div
        className="bg-card border rounded-3xl overflow-hidden"
        style={{ borderColor: "rgba(212,255,61,0.35)" }}
        data-testid="onboarding-embed"
      >
        <Cal
          namespace="onboarding-area"
          calLink={calLink}
          style={{ width: "100%", height: "700px", overflow: "scroll" }}
          config={{
            layout: "month_view",
            theme: "dark",
            name: user.name || "",
            email: user.email || "",
          }}
        />
      </div>
    </div>
  );
}

/* ─────────────── Prossime date ─────────────── */
function TabDates({ dates, orders }) {
  const [showOthers, setShowOthers] = useState(false);

  // Expand owned course ids (bundles / all-in unlock all)
  const ownedSet = new Set();
  (orders || []).forEach((o) => {
    const pid = o.product_id;
    if (!pid) return;
    if (pid === "all-in-experience") {
      ["controlla-la-tua-comunicazione-digitale", "social-media-marketing",
       "facebook-advanced", "customer-loyalty", "agenti-ai", "all-in-experience"].forEach((x) => ownedSet.add(x));
    } else if (pid.startsWith("bundle:")) {
      pid.replace("bundle:", "").split("+").forEach((x) => ownedSet.add(x));
    } else {
      ownedSet.add(pid);
    }
  });

  if (!dates.configured) {
    return (
      <div className="bg-card border border-white/10 rounded-2xl p-8 text-center" data-testid="tab-date-not-configured">
        <CalIcon size={32} className="mx-auto mb-4 text-white/40" />
        <div className="font-display text-white text-lg">Le prossime date arrivano a breve</div>
        <p className="text-white/60 text-sm mt-2 max-w-md mx-auto">
          Stiamo definendo il calendario delle prossime edizioni in aula. Sarai contattato via email non appena disponibili.
        </p>
      </div>
    );
  }
  if (dates.events.length === 0) {
    return (
      <div className="bg-card border border-white/10 rounded-2xl p-8 text-center" data-testid="tab-date-empty">
        <CheckCircle2 size={32} className="mx-auto mb-4" style={{ color: "#d4ff3d" }} />
        <div className="font-display text-white text-lg">Nessuna data pubblicata al momento</div>
        <p className="text-white/60 text-sm mt-2">Torna a controllare nei prossimi giorni.</p>
      </div>
    );
  }

  // Split events: mine (owned or unclassified) vs others
  const mine = [];
  const others = [];
  dates.events.forEach((ev) => {
    if (!ev.product_id || ownedSet.has(ev.product_id)) mine.push(ev);
    else others.push(ev);
  });

  const hasMine = mine.length > 0;
  const list = showOthers ? [...mine, ...others] : mine;

  return (
    <div data-testid="tab-date-list">
      {!hasMine && (
        <div className="bg-card border border-white/10 rounded-2xl p-6 mb-6 text-center">
          <p className="text-white/70 text-sm">
            Nessuna data pubblicata per i corsi che hai acquistato. Qui sotto vedi comunque tutte le prossime edizioni disponibili.
          </p>
        </div>
      )}
      <div className="space-y-3">
        {list.map((ev) => (
          <EventCard key={ev.uid || ev.start} ev={ev} owned={!ev.product_id || ownedSet.has(ev.product_id)} />
        ))}
      </div>
      {hasMine && others.length > 0 && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setShowOthers((v) => !v)}
            className="text-xs text-white/60 hover:text-white border border-white/10 rounded-full px-4 py-2 transition-colors"
            data-testid="toggle-other-dates"
          >
            {showOthers ? "Nascondi altre edizioni" : `Mostra altre ${others.length} edizioni (corsi diversi dai tuoi)`}
          </button>
        </div>
      )}
    </div>
  );
}

function EventCard({ ev, owned }) {
  const start = new Date(ev.start);
  const day = start.toLocaleDateString("it-IT", { day: "2-digit" });
  const month = start.toLocaleDateString("it-IT", { month: "short" }).toUpperCase();
  const icsUrl = `${API}/course-dates/${encodeURIComponent(ev.uid)}.ics`;
  return (
    <div
      className={`bg-card border rounded-2xl p-5 lg:p-6 flex items-start gap-5 ${owned ? "border-white/10" : "border-white/[0.06] opacity-70"}`}
      data-testid={`event-card-${ev.uid || ev.start}`}
    >
      <div className="text-center flex-shrink-0 w-16">
        <div className="font-display text-3xl text-white leading-none">{day}</div>
        <div className="text-xs mt-1 font-mono uppercase tracking-widest" style={{ color: owned ? "#d4ff3d" : "rgba(255,255,255,0.4)" }}>{month}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <div className="font-display text-white text-lg leading-snug">{ev.title || "Edizione in aula"}</div>
          {!owned && ev.product_id && (
            <span
              className="text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-full border"
              style={{ borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)" }}
              title="Questo corso non è tra i tuoi acquisti"
            >
              Non tuo
            </span>
          )}
        </div>
        {ev.location && (
          <div className="text-white/60 text-sm mt-1 flex items-center gap-1.5">
            <MapPin size={12} /> {ev.location}
          </div>
        )}
        {ev.description && (
          <p className="text-white/70 text-sm mt-3 leading-relaxed whitespace-pre-wrap">{ev.description}</p>
        )}
        {!ev.all_day && (
          <div className="text-white/40 text-xs mt-2 font-mono">
            {start.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
            {ev.end && " – " + new Date(ev.end).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
          </div>
        )}

        {owned && (
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href={icsUrl}
              download
              className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border border-white/15 text-white/85 hover:text-white hover:border-white/30 transition-colors"
              data-testid={`ics-download-${ev.uid || ev.start}`}
            >
              <Download size={11} /> Aggiungi al calendario (.ics)
            </a>
            <a
              href={buildGoogleCalendarUrl(ev)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border border-white/15 text-white/85 hover:text-white hover:border-white/30 transition-colors"
              data-testid={`gcal-add-${ev.uid || ev.start}`}
            >
              <CalIcon size={11} /> Google Calendar
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function buildGoogleCalendarUrl(ev) {
  const fmt = (iso) => {
    const d = new Date(iso);
    return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  };
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.title || "Edizione MyDigitalAction",
    dates: `${fmt(ev.start)}/${fmt(ev.end || ev.start)}`,
    details: ev.description || "",
    location: ev.location || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/* ─────────────── Chiedi info ─────────────── */
function TabInfoRequest() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setSending(true); setError(null);
    try {
      await studentApi.post("/auth/info-request", { subject, message });
      setSent(true);
      setSubject(""); setMessage("");
    } catch (err) {
      setError(err.response?.data?.detail || "Errore invio");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-card border rounded-2xl p-8 text-center max-w-xl mx-auto" style={{ borderColor: "rgba(212,255,61,0.35)" }} data-testid="info-request-sent">
        <CheckCircle2 size={32} className="mx-auto mb-4" style={{ color: "#d4ff3d" }} />
        <div className="font-display text-white text-xl">Richiesta inviata</div>
        <p className="text-white/60 text-sm mt-3">Ti risponderemo entro 24 ore lavorative all'indirizzo email del tuo account.</p>
        <button type="button" onClick={() => setSent(false)} className="btn-secondary mt-6" style={{ padding: "10px 18px", fontSize: 13 }}>
          Invia un'altra richiesta
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-card border border-white/10 rounded-2xl p-6 lg:p-8 max-w-2xl" data-testid="info-request-form">
      <div className="flex items-start gap-4 mb-6">
        <MessageCircle size={22} className="flex-shrink-0 mt-0.5" style={{ color: "#d4ff3d" }} />
        <div>
          <div className="font-display text-white text-lg">Chiedi informazioni</div>
          <p className="text-white/60 text-sm mt-2">
            Ti serve capire meglio un contenuto del corso? Vuoi cambiare la data della sessione in aula?
            Scrivici — rispondiamo entro 24 ore lavorative.
          </p>
        </div>
      </div>

      <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2">Oggetto</label>
      <input
        type="text"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        maxLength={200}
        required
        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30"
        placeholder="Es. Cambio data sessione, dubbi sul corso..."
        data-testid="info-subject"
      />

      <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2 mt-5">Messaggio</label>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        maxLength={5000}
        required
        rows={6}
        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 resize-y"
        placeholder="Scrivi qui la tua domanda..."
        data-testid="info-message"
      />

      {error && <div className="mt-4 text-red-400 text-sm">{error}</div>}

      <button
        type="submit"
        disabled={sending || !subject.trim() || !message.trim()}
        className="btn-primary mt-6 inline-flex items-center gap-2 disabled:opacity-50"
        data-testid="info-submit"
      >
        {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        Invia richiesta
      </button>
    </form>
  );
}
